/**
 * Types + utilitaires UI pour le panel Conversations.
 *
 * Le mock (generateMockConversations / tickMockConversations + bandes de
 * fake names/messages) a ete retire. La liste des conversations vient
 * de Cyrano (FastAPI `/api/creators/{id}/conversations` ou WS streamer
 * pour le live tail) — branchement a faire en Phase 6.
 *
 * On garde le shape `MockConversation` pour decrire ce que les cards
 * affichent. A renommer `Conversation` quand le backend sera cable.
 */

export type Stage =
  | "new"
  | "fan_low"
  | "fan_mid"
  | "fan_high"
  | "customer_low"
  | "customer_mid"
  | "customer_high"
  | "gfe"
  | "unknown";

export type Author = "fan" | "bot";

/**
 * Mode Cyrano ON-DEMAND par conversation (Copilote) :
 *  - "off"         : Cyrano INACTIF (défaut) — le chatteur gère, Cyrano ne répond pas
 *  - "relationnel" : Cyrano ACTIF, ton soft + gate paiement (« pour continuer, faut payer »)
 *  - "sexting"     : Cyrano ACTIF, ton chaud
 * Lancer un mode (POST /cyrano/start) arme + déclenche une réponse ; un envoi
 * humain OU POST /cyrano/stop repasse à "off". `isActiveMode()` = relationnel|sexting.
 * Backend `conversations.content_mode`. La conformité (mineur / red-line) reste
 * TOUJOURS prioritaire sur le ton côté pipeline.
 */
export type ContentMode = "off" | "relationnel" | "sexting";

/** true quand Cyrano est ACTIF sur la conv (un mode est lancé). */
export function isActiveMode(m: ContentMode | string | null | undefined): boolean {
  return m === "relationnel" || m === "sexting";
}

/**
 * Tag fan en shape compacte pour l'affichage inline (pastille à côté du pseudo
 * sur la card + dans le header du fan). Sous-ensemble du tag complet
 * (`ApiFanTag` dans atoms.ts) — juste de quoi rendre une pastille colorée.
 * Le backend l'hydrate dans la liste de conversations (cf projectToUi).
 */
export interface FanTagLite {
  id: string;
  name: string;
  /** Hex "#rrggbb" ou slug palette historique (primary/success/...). */
  color: string;
  is_system: boolean;
  // 🪶 Pas de `assignable` ici, contrairement à `ApiFanTag` : ces pastilles
  // sont rendues par `TagPills`, en lecture seule. Le champ n'aurait aucun
  // lecteur — et le backend ne le sert pas sur cette forme non plus.
}

/** Lien d'invitation tracké d'origine du fan (first-touch), hydraté par le
 *  backend conversations (liste). null = le fan n'est pas venu d'un de nos liens
 *  cockpit → pas de badge. */
export interface InviteLinkSourceLite {
  id: string;
  /** Nom du lien — peut être null (lien créé sans nom → libellé générique). */
  name: string | null;
}

/** Le compte Telegram par lequel le fan est entré, quand la créatrice en a
 *  plusieurs (Kayla, 16-09-2026 : « qu'on sache d'où il vient, de quel
 *  compte »). `username` null = Telegram n'a pas répondu → on montre le début
 *  de l'identifiant, jamais un nom inventé. */
export interface CompteDArriveeLite {
  business_connection_id: string;
  username: string | null;
}

/** Catégorie de « signal flash » détectée par Cyrano sur le dernier message du
 *  fan. payment 🟢 · content 🔵 · objection 🔴 · arousal 🌸 (== valeurs backend
 *  `conversations.live_signal`). */
export type SignalCategory = "payment" | "content" | "objection" | "arousal";

/** Couleur par catégorie — pastille de signal des panneaux de La Régie
 *  (`RegieContextPanel` / `RegieFocusHeader`). */
export const SIGNAL_COLOR: Record<SignalCategory, string> = {
  payment: "#22c55e", // vert
  content: "#3b82f6", // bleu
  objection: "#ef4444", // rouge
  arousal: "#ec4899", // rose
};

/** 🔵 CE QUI A COUPÉ CYRANO, ET QUE PERSONNE N'A ENCORE REPRIS.
 *
 *  Kayla, 21-08-2026 : « quand une personne a l'ultimatum, la carte clignote en
 *  bleu jusqu'à ce qu'une personne réponde ; quand c'est l'ultimatum du jeu, ça
 *  clignote en rose ».
 *
 *  Vocabulaire FERMÉ, le MÊME que les bandes du fil des coulisses — backend
 *  `conversations.moment_a_reprendre`, table
 *  `coulisses_du_fil._MOMENT_DU_GESTE`. ⛔ Aucun libellé ne voyage : le mot et
 *  la couleur se fabriquent ici.
 *
 *  🪶 Il se DÉRIVE côté backend du geste stampé sur le DERNIER message sortant,
 *  d'où sa propriété la plus utile : il s'éteint tout seul dès qu'un humain
 *  répond, puisque la réponse devient le dernier sortant. Le fan, lui, ne
 *  l'éteint pas — c'est justement là qu'il faut regarder. */
export type MomentAReprendre =
  | "ultimatum"
  | "relance_du_jeu"
  /** 🎁 Un fan vient de montrer son lot, et Cyrano s'est TU.
   *
   *  🩸 Le seul des trois qui ne vienne pas d'un message SORTANT : sur ce
   *  tour-là Cyrano n'écrit rien. Kayla, 22-08 : « j'ai pas envie qu'il
   *  reçoive ça en plein milieu d'une discussion et qu'il improvise […]
   *  qu'il y ait un petit indicateur, ça clignote en rose, pour qu'un
   *  chatteur puisse facilement repérer. » Il s'éteint au clic sur
   *  « Lancer le lot ». */
  | "lot_a_lancer"
  /** 💶 Cyrano vient de dire que c'est PAYANT — ou a laissé partir un montant.
   *
   *  Kayla, 01-09-2026 : « à chaque fois que Cyrano dit que c'est payant […]
   *  on laisse répondre. Ensuite on met Cyrano en stop, en pause. Il faut
   *  qu'il clignote. » */
  | "annonce_payante"
  /** 💶 Le fan a demandé le tarif, ou ce qu'on propose.
   *
   *  « c'est quoi tes prix, c'est combien, c'est quoi tes tarifs, tu proposes
   *  quoi comme contenu » — les quatre formes qu'elle a nommées. C'est le tour
   *  où une vente se gagne ou se perd, et Cyrano n'a pas les prix. */
  | "question_de_prix"
  /** 🤖 Le fan a cramé le bot — « t'es un bot, t'es une IA », « tu te répètes ».
   *
   *  🩸 Le seul des sept qui soit un grief sur la RELATION : il ne se répare ni
   *  avec un prix ni avec un remboursement, et c'est pour ça qu'il a sa
   *  couleur à lui. */
  | "accusation_bot"
  /** 🖼️ Le fan RÉCLAME un média — « envoie-moi une photo », « montre ».
   *
   *  Kayla, 02-09-2026 : « ajoute la demande de contenu aussi ». C'est le même
   *  tour que `question_de_prix` vu de l'autre bout — il dit ce qu'il veut, et
   *  Cyrano n'a ni le catalogue ni les prix pour y répondre. D'où le même
   *  vert : ces deux-là parlent de la même vente. */
  | "demande_de_contenu"
  /** 🧵 Un FLOW s'est tu — le fan a répondu à sa dernière question, ou s'est
   *  tu jusqu'à l'arrêt — et personne n'a encore repris.
   *
   *  Kayla, 02-09-2026 : « tu fais un clignotement quand un flow s'est arrêté,
   *  et pareil quand on répond ». Dérivé côté Cyrano du dernier SORTANT
   *  (`sender_source = flow`) + aucun run vivant ; il s'éteint dès qu'un
   *  humain écrit. */
  | "flow_termine";

/** Couleur du CLIGNOTEMENT par moment.
 *
 *  ⚠️ Les mêmes valeurs que `SIGNAL_COLOR` : le bleu, le rose, le vert et le
 *  rouge du cockpit sont ceux-là, et en inventer une nuance de plus ferait
 *  deux bleus côte à côte sur le même écran. */
export const MOMENT_COLOR: Record<MomentAReprendre, string> = {
  ultimatum: "#3b82f6", // bleu
  relance_du_jeu: "#ec4899", // rose
  // 🪶 Le MÊME rose que la relance : les deux appartiennent au jeu, et une
  //    troisième nuance ferait deux roses côte à côte sur le même écran.
  lot_a_lancer: "#ec4899", // rose
  // 💶 Le vert de `SIGNAL_COLOR.payment` : ces deux-là parlent d'ARGENT, et
  //    c'est la même famille. Une seule couleur pour les deux, exactement
  //    comme le jeu n'a qu'un rose — ce qui distingue les deux moments se lit
  //    dans le fil, pas dans la couleur d'une carte.
  annonce_payante: "#22c55e", // vert
  question_de_prix: "#22c55e", // vert
  demande_de_contenu: "#22c55e", // vert
  // 🤖 Le rouge de `SIGNAL_COLOR.objection`. Il n'a rien à voir avec l'argent,
  //    et une carte qui clignote rouge dans la grille doit se lire d'un coup
  //    d'œil comme « celle-là d'abord ».
  accusation_bot: "#ef4444", // rouge
  // 🧵 Le bleu de l'ultimatum : même famille — Cyrano (ici le script) s'est
  //    tu, un humain reprend. Pas de cinquième couleur.
  flow_termine: "#3b82f6", // bleu
};

export interface MockConversation {
  id: string;
  fan_name: string;
  /** Photo de profil Telegram du fan (User.avatar_url cote backend). */
  fan_avatar_url: string | null;
  /** 🟢 Statut Telegram du fan, déjà vieilli par le backend — `panels/fan/presence.ts`. */
  fan_presence?: string | null;
  fan_presence_vu_a?: string | null;
  creator_id: string;
  /** Slug stable Cyrano = Convex slug — cle de jointure cross-data-plane. */
  creator_slug: string;
  creator_name: string;
  creator_avatar_url: string | null;
  creator_color: string;
  /** Code langue UPPERCASE detecte par le backend (profile -> memory facts).
   *  null quand on ne sait pas — le cockpit affiche alors RIEN plutot
   *  qu'un faux drapeau anglais par defaut. */
  language: string | null;
  last_message: string;
  /** 🌍 `last_message` déjà traduit, `{ langue: texte }` — l'aperçu le lit
   *  dans la langue du chatteur (`apercuTraduit.ts`). Absent = rien. */
  last_message_traductions?: Record<string, string> | null;
  last_author: Author;
  last_message_at: number;
  /** Quand le fan est arrive (premier message). Sert au filtre time-window. */
  created_at: number;
  /** 0 = read, sinon nombre de messages non-lus accumules. */
  unread_count: number;
  stage: Stage;
  has_active_order: boolean;
  message_count: number;
  /** Total depense par ce fan (cents). Affiche en gros sur la card. */
  revenue_cents: number;
  currency: "USD" | "EUR";
  /** Etat pause du bot pour cette conversation (Redis `bot_paused:{chat_id}`).
   *  null = on ne peut pas savoir (pas de chat_id) — bouton desactive.
   *  IMPORTANT: c'est un kill-switch worker, pas l'etat "agent en pause"
   *  que le user controle au quotidien. Pour ca → `is_cyrano_paused`. */
  bot_is_paused: boolean | null;
  /** True quand Cyrano est explicitement en pause sur la conv (column DB
   *  `conversations.is_cyrano_paused`). C'est cet etat que le user voit
   *  "STOP/GO" dans le bottom bar ConvStatusBar du fan panel + sur la
   *  card grid. Auto-set a True par flow_executor pendant un run. */
  is_cyrano_paused: boolean;
  /** Flow run actif sur cette conversation, si l'endpoint conversations
   *  list l'hydrate. Quand present, la card affiche la progression du flow
   *  au lieu du statut agent (Cyrano + STOP/GO). */
  active_flow_run?: {
    flow_id: string;
    flow_name: string;
    progress_percent: number;
    completed_nodes: number;
    total_nodes: number;
    /** ISO : depuis quand le run est au step courant (« à ce step depuis X »). */
    current_node_entered_at?: string | null;
  } | null;
  /** Tags fan assignés (scope creator de la conv), hydratés par l'endpoint
   *  liste. Triés par nom, archivés exclus. Rendus en pastilles à côté du
   *  pseudo (card + header) et servent au filtre par tag. [] si aucun. */
  tags: FanTagLite[];
  /** Lien d'invitation tracké d'origine du fan (first-touch). null/absent = pas
   *  de badge « vient du lien X » (card + header). Optionnel — cohérent avec
   *  `active_flow_run` (hydraté par l'endpoint liste, absent des fixtures). */
  invite_link_source?: InviteLinkSourceLite | null;
  /** Le compte Telegram de la créatrice par lequel le fan est entré — servi
   *  seulement quand elle en a plusieurs. Badge « compte @tristavoid » à côté
   *  du lien (header). null/absent = rien à préciser. */
  compte_d_arrivee?: CompteDArriveeLite | null;
  /** True quand le fan est CHAUD (signal buy_intent/aroused) ET que le dernier
   *  message entrant attend une réponse. Backend `conversations.needs_attention` ;
   *  effacé quand un chatteur répond. Optionnel : la projection le pose toujours
   *  (`?? false`), les fixtures de test peuvent l'omettre. */
  needs_attention?: boolean;
  /** Cause lisible pour l'infobulle ("buy_intent" | "aroused" | ...). */
  needs_attention_reason?: string | null;
  /** Signal détecté par Cyrano (unified_signals) sur le DERNIER message du fan
   *  (🟢/🔵/🔴/🌸) — lu par les panneaux de La Régie. Backend
   *  `conversations.live_signal` + event SSE `signal_flash`. Re-évalué à chaque
   *  message ; effacé quand un humain répond. */
  live_signal?: SignalCategory | null;
  /** Niveau d'énergie du fan [0..1] au moment du signal, affiché en % par
   *  `RegieContextPanel`. Backend `conversations.live_signal_energy`. */
  live_signal_energy?: number | null;
  /** 👁 L'ATTENTION DU FAN — l'œil et le pourcentage rendus sur la carte, et
   *  dans l'en-tête du fan. Son délai de réponse et la taille de son texte,
   *  comparés à SON propre rythme, cumulés sur une dizaine de tours. Backend
   *  `conversations.attention_niveau`. ABSENT ou `null` ⇒ rien d'affiché (cf.
   *  `NiveauDAttention`) — jamais un niveau neutre inventé.
   *  ⚠️ À ne pas confondre avec `live_signal_energy` juste au-dessus. */
  attention?: number | null;
  attention_palier?: string | null;
  attention_tendance?: string | null;
  attention_observations?: number | null;
  attention_mesure_le?: string | null;
  /** 🔵 Ce qui a coupé Cyrano et que personne n'a repris — la carte clignote.
   *  Backend `conversations.moment_a_reprendre`. ABSENT = le backend ne sait
   *  pas encore le dire (le cockpit se déploie AVANT Cyrano) ; `null` = rien à
   *  reprendre. Les deux se rendent pareil — pas de clignotement. */
  moment_a_reprendre?: MomentAReprendre | null;
  /** 💶 L'offre en l'air (montant, devise, depuis quand) — cf. `ApiConversationListItem`. */
  offre_en_attente?: { montant_cents: number; devise: string; depuis: string } | null;
  /** 🎯 Le rang de la file du chatteur (0 · 1 · 2) et sa raison lisible —
   *  `null`/absent = pas de rang. Cf. `fileCourte.ts`. */
  priorite_rang?: number | null;
  priorite_raison?: string | null;
  /** 🎯 Le signal du tri `signaux` (nom et date) — absent sur les autres tris. */
  signal?: string | null;
  signal_le?: string | null;
  /** 📌 Qui se l'est réservée (`workos_user_id`), et si ça tient en ce moment
   *  (son épingleur est en poste). Cf. `pileDuChatteur.reservation`. */
  epinglee_par?: string | null;
  epingle_protegee?: boolean;
  /** 📋 Qui la TIENT — le chatteur dont la pile la porte en ce moment
   *  (`workos_user_id` de l'attribution ouverte), épinglée ou non. Absent ou
   *  `null` = personne. Cf. `pileDuChatteur.reservation`. */
  tenue_par?: string | null;
  /** Mode de contenu Copilote (auto/relationnel/sexting) forcé sur cette conv.
   *  Backend `conversations.content_mode`. La projection le pose toujours
   *  (`?? "auto"`) ; les fixtures de test peuvent l'omettre. */
  content_mode?: ContentMode;
  /** True quand une proposition de décision (mode PROPOSE, décisionnel) est
   *  active sur cette conversation → chip « Décision » sur la carte. Backend
   *  `conversations.pending_decision` + event SSE `decision_proposal`.
   *  Optionnel/absent = pas de décision en attente. */
  pending_decision?: boolean;
  /** Libellé compact du variant recommandé (1er de la liste), pour le chip
   *  liste (« Décision · <label> », tronqué si besoin). null/absent = chip
   *  générique « Décision » sans détail. */
  pending_decision_label?: string | null;
  /** Peut-on encore ÉCRIRE à ce fan ? Dérivé par le backend des mêmes prédicats
   *  que le masquage des conversations injoignables (connexion Business active,
   *  fenêtre d'activité, sonde d'envoi). `false` → pastille « Inactive » sur la
   *  carte, pour ne pas écrire dans le vide dans la vue « toutes les
   *  conversations ». `null`/absent = indéterminé → aucune pastille. */
  is_reachable?: boolean | null;
  /** 🐦 D'où vient la conversation : `telegram` (le défaut) ou `twitter`.
   *
   *  Optionnel : le cockpit se déploie AVANT Cyrano, et pendant cette fenêtre
   *  le champ est absent. « Absent » veut dire « on ne sait pas », donc aucune
   *  pastille — jamais « Telegram par défaut », qui serait une affirmation. */
  canal?: string | null;
  /** 🌉 Ce fan a une identité X, même si sa conversation vient de Telegram.
   *
   *  Kayla, 04-09-2026 : « si la personne est sur Telegram après, on peut voir
   *  qu'avant, elle était sur Twitter et qu'on lui écrit sur Twitter ou alors
   *  on lui écrit sur Telegram. » `canal` fige l'origine ; celui-ci dit le
   *  passé — et il est vrai exactement quand le canal X est encore joignable
   *  dans le composeur (les deux se dérivent de `users.twitter_user_id`).
   *
   *  Optionnel, comme `canal` : absent = backend pas encore déployé. */
  aussi_sur_x?: boolean | null;
  /** Le workflow qui tourne EN CE MOMENT sur cette conversation, hydraté par
   *  l'endpoint liste. `null`/absent = aucun parcours ouvert → la carte ne
   *  porte pas de bandeau.
   *
   *  🪤 Optionnel, et strictement : le cockpit se déploie AVANT Cyrano, donc
   *  pendant la fenêtre de rebuild le champ n'existe pas dans la réponse. Un
   *  bandeau rendu sur `undefined` peindrait « aucun workflow » partout comme
   *  si c'était mesuré. Absent = on ne sait pas, on n'affiche rien. */
  workflow_actif?: {
    /** Le nom du parcours, tel que son dossier le porte
     *  (`app/workflows/<nom>/`) — ex. « montrer_l_offre ». */
    nom: string;
    /** Ce que le parcours s'apprête à demander à ce tour (le créneau visé),
     *  ou null quand il n'a rien à demander maintenant (cadence, file vide). */
    etape?: string | null;
    /** ISO : depuis quand ce parcours est ouvert. */
    ouvert_le?: string | null;
  } | null;
  /** 🔔 Le déclenchement de workflow NON PRIS de cette conversation.
   *
   *  Kayla, 16-08-2026 : « quand un workflow est déclenché, il y a le bouton
   *  je prends ; dès que je clique dessus le signal se cache, mais tant que
   *  j'ai pas cliqué, il reste ».
   *
   *  ⛔ À ne pas confondre avec `workflow_actif` : celui-là dit qu'un parcours
   *  TOURNE et s'éteint quand il se referme — tout seul, que quelqu'un ait
   *  regardé ou non. Celui-ci dit qu'une porte a MORDU et que personne ne l'a
   *  acquitté ; il survit à la fermeture, au redémarrage du pod et au
   *  changement d'appareil. Un parcours `temoin` n'ouvre AUCUNE instance :
   *  il n'a jamais de `workflow_actif`, et c'est ce champ qui porte tout.
   *
   *  🪤 Optionnel pour la même raison que son voisin : absent = le backend
   *  n'est pas encore déployé, on ne sait pas, on n'affiche rien. */
  signal_workflow?: {
    /** Le parcours du déclenchement le plus récent non pris. */
    nom: string;
    /** Sa porte — pourquoi ça s'est levé. */
    declencheur: string;
    /** ISO : quand ce dernier déclenchement a été observé. */
    depuis: string;
    /** `> 1` = ça a remordu entre deux regards ; un clic solde tout. */
    combien: number;
  } | null;
}

/**
 * Mapping code langue → code pays ISO 3166-1 alpha-2 (lowercase).
 * Utilise par le composant <Flag/> qui rend un SVG via flag-icons.
 * Windows ne rend pas les drapeaux emoji nativement.
 */
const COUNTRY_BY_LANG: Record<string, string> = {
  EN: "gb",
  FR: "fr",
  ES: "es",
  DE: "de",
  IT: "it",
  PT: "pt",
  NL: "nl",
  PL: "pl",
  RU: "ru",
  JA: "jp",
  KO: "kr",
  ZH: "cn",
  AR: "sa",
  TR: "tr",
  SV: "se",
  FI: "fi",
  EL: "gr",
  HE: "il",
  // 26-09-2026 : le drapeau se pose désormais sous chaque bulle traduite, et
  // Jev lit 30 langues. Celles-ci manquaient et rendaient un code en lettres.
  UK: "ua",
  RO: "ro",
  CS: "cz",
  HU: "hu",
  DA: "dk",
  NO: "no",
  BG: "bg",
  HR: "hr",
  SK: "sk",
  HI: "in",
  ID: "id",
  TH: "th",
  VI: "vn",
  FA: "ir",
  MS: "my",
  TL: "ph",
};

/** Code ISO 3166-1 alpha-2 (lowercase) pour le drapeau, ou null quand
 *  on ne sait pas. Les composants doivent skipper l'affichage du drapeau
 *  sur null plutot que rendre un fallback fantome. */
export function flagFor(lang: string | null | undefined): string | null {
  if (!lang) return null;
  return COUNTRY_BY_LANG[lang.toUpperCase()] ?? null;
}

/**
 * Format `cents` → string court, type "$1.2k", "€340", "—" si zero.
 */
export function formatRevenue(cents: number, currency: "USD" | "EUR"): string {
  const symbol = currency === "EUR" ? "€" : "$";
  if (cents === 0) return "—";
  const dollars = cents / 100;
  if (dollars >= 1000) {
    const k = dollars / 1000;
    return `${symbol}${k < 10 ? k.toFixed(1) : Math.round(k)}k`;
  }
  return `${symbol}${Math.round(dollars)}`;
}
